import { getStoredJwt } from './auth';

type ApiMethod = 'GET' | 'POST';

interface ApiRequestOptions {
  method?: ApiMethod;
  body?: unknown;
  auth?: boolean;
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const API_URL = process.env.REACT_APP_API_URL || '';

export const getApiUrl = (endpoint: string) => `${API_URL}${endpoint}`;

const getHeaders = (auth: boolean) => {
  const jwt = getStoredJwt();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (auth && jwt) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  return headers;
};

const parseResponse = async (response: Response) => {
  const responseText = await response.text();

  if (!responseText) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch (error) {
    return responseText;
  }
};

export const apiRequest = async <T>(
  endpoint: string,
  { method = 'GET', body, auth = true }: ApiRequestOptions = {}
): Promise<T> => {
  const response = await fetch(getApiUrl(endpoint), {
    method,
    mode: 'cors',
    headers: getHeaders(auth),
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const data = await parseResponse(response);

  if (!response.ok) {
    const message =
      typeof data === 'object' &&
      data !== null &&
      'error' in data &&
      typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : response.statusText || 'Request failed.';

    throw new ApiError(message, response.status, data);
  }

  return data as T;
};

export const getJson = <T>(endpoint: string, auth = true) =>
  apiRequest<T>(endpoint, { method: 'GET', auth });

export const postJson = <T>(endpoint: string, body?: unknown, auth = true) =>
  apiRequest<T>(endpoint, { method: 'POST', body, auth });
